/**
 * Shapes of the public API as documented in docs/API.md ("Booking page",
 * "Availability", "Appointments" public routes). The API is implemented
 * separately; these interfaces are the contract the booking page relies
 * on, and nothing here is wider than what the docs promise.
 */
import type { AppointmentStatus } from '@shared/domain';
import type { AccentName } from '@shared/branding';

export interface PublicLocation {
  id: string;
  name: string;
  timezone: string;
  address: string | null;
  phone: string | null;
}

export type QuestionKind = 'text' | 'textarea' | 'choice' | 'boolean';

/** What a yes/no question offers, and the only answers the API accepts for one. */
export const BOOLEAN_CHOICES = ['Yes', 'No'];

/** Something the business asks before this service is booked. */
export interface PublicQuestion {
  id: string;
  label: string;
  help_text: string | null;
  kind: QuestionKind;
  /** The options of a `choice` question; empty for every other kind. */
  choices: string[];
  required: boolean;
}

export interface PublicService {
  id: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  /**
   * With dual pricing (0045) this is the CARD price — the business's
   * advertised price, unchanged by the setting — and `cash_price_cents`
   * is the discounted cash price beside it.
   */
  price_cents: number;
  cash_price_cents?: number;
  /**
   * With a card fee (0048) `price_cents` is THE price — cash, debit and
   * prepaid — and this is the credit card total, fee included.
   */
  card_price_cents?: number;
  /** Asked for before the appointment; absent when there is none. */
  deposit_cents?: number;
  currency: string;
  /** Location coverage via providers; may be absent, in which case it is derived from providers. */
  location_ids?: string[];
  /** Asked on the details step; may be absent, in which case nothing is asked. */
  questions?: PublicQuestion[];
}

export interface PublicProvider {
  id: string;
  name: string;
  bio: string | null;
  photo_url: string | null;
  location_id: string;
  service_ids: string[];
}

export interface PublicOrganization {
  name: string;
  slug: string;
  timezone: string;
  currency: string;
}

/** `booking_pages` columns exposed to customers. */
export interface PublicPage {
  display_name: string | null;
  headline: string | null;
  description: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  accent: AccentName | string;
  /**
   * The language this page is published in (0038). The visitor's own
   * choice wins over it; see `src/shared/i18n.ts`.
   */
  locale?: string;
  contact_email: string | null;
  contact_phone: string | null;
  website_url: string | null;
  address: string | null;
  /** e.g. { instagram: 'https://instagram.com/...' } */
  social_links: Record<string, string>;
  booking_policy: string | null;
  cancellation_policy: string | null;
  payment_instructions: string | null;
  /** Accepted payment methods, shown to customers with `payment_instructions`. */
  accepts_cash: boolean;
  /** How this business handles card processing costs (0045). */
  payment_pricing?: { mode: 'absorb' | 'dual' | 'card_fee'; cash_discount_bps: number; card_fee_bps?: number; dual: boolean; card_fee?: boolean };
  /** The trade this page is about: the industry page's own, or the business's primary trade. */
    industry?: string | null;
  accepts_card_in_person: boolean;
  /** Zelle recipient (phone, email or name), or null when Zelle isn't accepted. */
  zelle_recipient: string | null;
  show_prices: boolean;
  allow_provider_choice: boolean;
  /** Provider preference (0051): the options this business offers, or null when it offers none. */
  provider_preference?: { options: string[] } | null;
  min_notice_minutes: number;
  max_advance_days: number;
  cancel_notice_minutes: number;
  /** Whether this business offers to take a name when a day is full. */
  waitlist_enabled: boolean;
}

/** A class a customer can still join, as the public page shows it. */
export interface PublicSession {
  id: string;
  service_id: string;
  provider_id: string | null;
  provider_selection?: 'specific' | 'next_available';
  provider_assignment_status?: 'assigned' | 'unassigned';
  location_id: string;
  starts_at: string;
  ends_at: string;
  timezone: string;
  seats_left: number;
  capacity: number;
}

/** Another face of the same business: one of its industry pages. */
export interface PublicIndustryPage {
  page_slug: string;
  industry: string;
  label: string | null;
}

export interface ActivePageResponse {
  status: 'active';
  /** False when online booking is switched off in this environment: show the page, do not book. */
  booking_enabled: boolean;
  /** Whether to show the "Powered by Book with Assemble" attribution (removed on paid plans). */
  powered_by: boolean;
  page: PublicPage;
  organization: PublicOrganization;
  /** The business's other published industry pages, for navigation. */
  pages: PublicIndustryPage[];
  /** True when this business also publishes a shop at /:slug/shop (0040). */
  shop?: boolean;
  /** The service request intake this business publishes (0053), or null. */
  service_request?: { name: string; instructions: string | null } | null;
  locations: PublicLocation[];
  services: PublicService[];
  providers: PublicProvider[];
}

export interface UnavailablePageResponse {
  status: 'unavailable';
  page: {
    display_name: string | null;
    unavailable_message: string | null;
    locale?: string;
    contact_email?: string | null;
    contact_phone?: string | null;
    website_url?: string | null;
    address?: string | null;
    logo_url?: string | null;
  };
}

export type PublicPageResponse = ActivePageResponse | UnavailablePageResponse;

export interface Slot {
  /** Instant, ISO 8601 UTC. */
  starts_at: string;
  ends_at: string;
  provider_id: string;
}

export interface AvailabilityResponse {
  /** The LOCATION's calendar day. */
  date: string;
  /** The LOCATION's time zone. */
  timezone: string;
  slots: Slot[];
  /** True when the customer's provider preference is why there is nothing: nobody eligible offers this service. */
  no_provider_for_preference?: boolean;
}

/** Appointment JSON as the customer sees it (no internal notes). */
export interface PublicAppointment {
  financial_policy?:import('./components/FinancialDisclosure').FinancialQuote;
  id: string;
  status: AppointmentStatus;
  starts_at: string;
  ends_at: string;
  /** Zone the appointment was booked in (the location's). */
  timezone: string;
  service_id: string;
  provider_id: string;
  location_id: string;
  customer_notes: string | null;
  price_cents: number;
  /** Payment pricing as this booking was quoted (0045); null before it. */
  pricing_mode?: 'absorb' | 'dual' | 'card_fee' | null;
  card_fee_bps?: number | null;
  card_price_cents?: number | null;
  cash_price_cents?: number | null;
  price_kind?: 'cash' | 'card' | null;
  deposit_cents?: number;
  deposit_paid_at?: string | null;
  currency: string;
  /** Bookkeeping by the business; customers only ever see "Paid". */
  payment_status?: 'unpaid' | 'paid' | 'waived' | 'refunded' | 'card_on_file' | 'deposit_paid' | 'partially_paid' | 'partially_refunded' | 'payment_failed' | 'cancellation_fee_eligible' | 'cancellation_fee_paid';
  /** 'YYYY-MM-DDTHH:MM' in `timezone`. */
  local_starts_at: string;
  local_ends_at: string;
  service_name: string;
  provider_name: string;
  location_name: string;
  customer_name?: string;
  rescheduled_from_id?: string | null;
  rescheduled_to_id?: string | null;
  cancelled_at?: string | null;
  /** When this customer confirmed they are coming (0042). */
  customer_confirmed_at?: string | null;
  /** When this customer told us they had arrived (0043), or null. Not a status. */
  checked_in_at?: string | null;
  /** Present on creation (and, ideally, on reschedule). */
  manage_token?: string;
}

export interface CreateAppointmentRequest {
  financial_policy_token?:string; accept_financial_policy?:boolean;
  /** Which of the two published prices the customer chose. A path, not an amount. */
  price_kind?: 'cash' | 'card';
  service_id: string;
  provider_id?: string;
  /** A specific person, or a capacity-reserved next-available booking that the team claims later. */
  provider_selection?: 'specific' | 'next_available';
  /** The customer's provider preference (0051), where the business offers one. */
  provider_preference?: string;
  starts_at: string;
  customer: { name: string; email: string; phone?: string; sms_opt_in?: boolean; timezone?: string; locale?: string };
  notes?: string;
  answers?: { question_id: string; answer: string }[];
}

export interface AppointmentResponse {
  appointment: PublicAppointment;
  service?: Partial<PublicService> & { name?: string };
  provider?: Partial<PublicProvider> & { name?: string };
  location?: Partial<PublicLocation> & { name?: string };
}

export interface CustomerDetails {
  sms_opt_in?: boolean;
  name: string;
  email: string;
  phone: string;
  notes: string;
}

